import { useEffect, useState } from 'react';
import type { LoyaltyEarnModel } from '@salonomy/types';
import {
  Button,
  Card,
  Field,
  SegmentedControl,
  Select,
  Spinner,
  TextInput,
  Toggle,
} from '@salonomy/ui';
import { useLoyaltyProgram, useUpsertLoyaltyProgram } from '../../data/loyalty.js';
import { useAuth } from '../../lib/auth.js';
import { money } from '../../lib/format.js';
import { penceToPounds, poundsToPence } from '../till/money.js';
import { useToast } from '../../lib/toast.js';

const EARN_MODELS: Array<{ value: LoyaltyEarnModel; label: string }> = [
  { value: 'points_per_spend', label: 'Points per spend' },
  { value: 'visit_stamps', label: 'Visit stamps' },
];

// "Never" + a useful span of expiry windows. NULL = points never expire (the expiry job is a
// fast-follow; the column lands now). We map "never" → null on save.
const EXPIRY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'never', label: 'Never' },
  { value: '6', label: 'After 6 months' },
  { value: '12', label: 'After 12 months' },
  { value: '18', label: 'After 18 months' },
  { value: '24', label: 'After 24 months' },
  { value: '36', label: 'After 36 months' },
];

/**
 * Per-salon LOYALTY program config (manager-only). Mirrors ReviewSettingsPanel: local form state seeds
 * from the server program and reconciles whenever it (re)loads; a single Save sends a partial
 * UpsertLoyaltyProgram. The server is authoritative — this panel never derives a balance, a points→£
 * value or a reward; it only edits the inputs the till and accrual run on.
 *
 * RBAC: the host (SettingsPage) renders this in the manager branch, but we accept `canEdit` to disable
 * defensively and show a non-manager an honest note rather than a bare 403 as their first feedback.
 *
 * Two earn models drive which rate fields matter (both are always sent — the inert ones are harmless):
 *  - points_per_spend: pointsPerPound earned per whole £ of ex-VAT spend; redeemPencePerPoint sets what
 *    each point is worth at the till.
 *  - visit_stamps: a digital loyalty card — stampsPerReward stamps unlock one rewardValuePence reward.
 */
export function LoyaltySettingsPanel({ canEdit = true }: { canEdit?: boolean }) {
  const { isManager } = useAuth();
  const allowed = canEdit && isManager;
  const { data, isLoading, isError } = useLoyaltyProgram(allowed);
  const update = useUpsertLoyaltyProgram();
  const toast = useToast();

  const [enabled, setEnabled] = useState(false);
  const [earnModel, setEarnModel] = useState<LoyaltyEarnModel>('points_per_spend');
  const [pointsPerPound, setPointsPerPound] = useState('1');
  const [redeemPencePerPoint, setRedeemPencePerPoint] = useState('1');
  const [stampsPerReward, setStampsPerReward] = useState('10');
  const [rewardValue, setRewardValue] = useState(''); // £ string
  const [minRedeem, setMinRedeem] = useState('0');
  const [expiry, setExpiry] = useState('never'); // 'never' | months
  const [busy, setBusy] = useState(false);

  // Seed/reconcile the form from the server whenever the program loads or changes.
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setEarnModel(data.earnModel);
    setPointsPerPound(String(data.pointsPerPound));
    setRedeemPencePerPoint(String(data.redeemPencePerPoint));
    setStampsPerReward(String(data.stampsPerReward));
    setRewardValue(penceToPounds(data.rewardValuePence));
    setMinRedeem(String(data.minRedeemPoints));
    setExpiry(data.pointsExpireMonths == null ? 'never' : String(data.pointsExpireMonths));
  }, [data]);

  // Honest note for a non-manager — never the editable form.
  if (!allowed) {
    return (
      <Card>
        <div className="section-title" style={{ marginBottom: 2 }}>
          Loyalty
        </div>
        <div className="muted" style={{ fontSize: 13, padding: '4px 0' }}>
          Only an owner or manager can set up the loyalty program (how points are earned and what
          they redeem for).
        </div>
      </Card>
    );
  }

  async function save() {
    const ppp = Number(pointsPerPound);
    const rpp = Number(redeemPencePerPoint);
    const spr = Number(stampsPerReward);
    const minR = Number(minRedeem);
    const rewardPence = poundsToPence(rewardValue) ?? 0;

    if (
      !Number.isInteger(ppp) ||
      ppp < 0 ||
      !Number.isInteger(rpp) ||
      rpp < 0 ||
      !Number.isInteger(spr) ||
      spr < 1 ||
      !Number.isInteger(minR) ||
      minR < 0
    ) {
      toast.error('Check the rates — they must be whole, non-negative numbers.');
      return;
    }

    setBusy(true);
    try {
      // Send the whole config (a partial is allowed, but sending all fields keeps the inert model's
      // values intact when the salon flips earn models later). The server re-validates.
      await update.mutateAsync({
        enabled,
        earnModel,
        pointsPerPound: ppp,
        redeemPencePerPoint: rpp,
        stampsPerReward: spr,
        rewardValuePence: rewardPence,
        minRedeemPoints: minR,
        pointsExpireMonths: expiry === 'never' ? null : Number(expiry),
      });
      toast.success('Loyalty settings saved');
    } catch {
      toast.error('Could not save your loyalty settings.');
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading loyalty settings…
          </span>
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load your loyalty settings. Please try again.
        </div>
      </Card>
    );
  }

  const perSpend = earnModel === 'points_per_spend';

  return (
    <Card>
      <div className="section-title" style={{ marginBottom: 2 }}>
        Loyalty
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Reward repeat clients with points or stamps. Points are a perk, not money — redeeming them
        applies a discount at the till. The till always shows the figure the server works out.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <Toggle checked={enabled} onChange={setEnabled} label="Run a loyalty program" />
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            When off, no points are earned and nothing can be redeemed. Existing balances are kept.
          </div>
        </div>

        <Field
          label="How clients earn"
          hint={
            perSpend
              ? 'Points accrue on every receipt, based on the amount spent (before VAT).'
              : 'A digital loyalty card — one stamp per visit, unlocking a reward.'
          }
        >
          <SegmentedControl<LoyaltyEarnModel>
            options={EARN_MODELS}
            value={earnModel}
            onChange={setEarnModel}
          />
        </Field>

        {perSpend ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Points earned per £1 spent" hint="Whole points per pound (before VAT).">
              <TextInput
                inputMode="numeric"
                value={pointsPerPound}
                onChange={(e) => setPointsPerPound(e.target.value)}
                placeholder="1"
              />
            </Field>
            <Field
              label="Each point is worth (pence)"
              hint={
                Number(redeemPencePerPoint) > 0
                  ? `e.g. 100 points = ${money(100 * (Number(redeemPencePerPoint) || 0))} off.`
                  : 'How much a point reduces the bill when redeemed.'
              }
            >
              <TextInput
                inputMode="numeric"
                value={redeemPencePerPoint}
                onChange={(e) => setRedeemPencePerPoint(e.target.value)}
                placeholder="1"
              />
            </Field>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Stamps to unlock a reward" hint="One stamp per visit.">
              <TextInput
                inputMode="numeric"
                value={stampsPerReward}
                onChange={(e) => setStampsPerReward(e.target.value)}
                placeholder="10"
              />
            </Field>
            <Field label="Reward value (£)" hint="What the unlocked reward is worth.">
              <TextInput
                inputMode="decimal"
                value={rewardValue}
                onChange={(e) => setRewardValue(e.target.value)}
                placeholder="0.00"
              />
            </Field>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field
            label="Minimum points to redeem"
            hint="Clients can only redeem once they hold at least this many. 0 = no minimum."
          >
            <TextInput
              inputMode="numeric"
              value={minRedeem}
              onChange={(e) => setMinRedeem(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field label="Points expire" hint="When unused points lapse.">
            <Select value={expiry} onChange={(e) => setExpiry(e.target.value)}>
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div>
          <Button onClick={save} disabled={busy}>
            Save settings
          </Button>
        </div>
      </div>
    </Card>
  );
}
